import { Check, Globe } from 'lucide-react';

import { Menu } from '@base-ui/react/menu';

import { LOCALE_LABEL, LOCALE_SHORT, LOCALES, useTranslation } from '@core/i18n';
import { cn } from '@core/utils/cn';

/**
 * A menu rather than a segmented control.
 *
 * Three locales already crowd a segmented control at collapsed nav widths, and the list
 * will grow. A menu also lets each language be named in its own script, which a
 * two-letter segment cannot do.
 */
export function LanguageSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { locale, setLocale, t } = useTranslation();

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={t.common.language}
        className={cn(
          'text-fg-muted flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px]',
          'hover:bg-fill-subtle hover:text-fg transition-colors duration-150',
          'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
          collapsed && 'justify-center px-0',
        )}
      >
        <Globe className="size-4 shrink-0" />
        {!collapsed && <span className="font-medium">{LOCALE_SHORT[locale]}</span>}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="top" align="start" sideOffset={8} className="z-50">
          <Menu.Popup
            className={cn(
              'rounded-card bg-overlay shadow-e2 min-w-44 origin-(--transform-origin) p-1.5',
              'ring-separator ring-1 ring-inset',
              'ease-spring transition-[transform,opacity] duration-200',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              'motion-reduce:transition-none',
            )}
          >
            {LOCALES.map((code) => (
              <Menu.Item
                key={code}
                onClick={() => setLocale(code)}
                className={cn(
                  'grid cursor-default grid-cols-[1.25rem_1fr] items-center gap-2',
                  'rounded-md px-2.5 py-2 text-[14px] outline-none select-none',
                  'data-highlighted:bg-fill-subtle data-highlighted:text-fg',
                )}
              >
                {locale === code ? <Check className="text-accent size-4" /> : <span />}
                <span>{LOCALE_LABEL[code]}</span>
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
